import * as React from 'react'
import * as z from 'zod'
import { format } from '@basehub/utils/format'

import type { Block, IconBlock } from '@basehub/data/replicache/index'
import { Icon, supportedSvgTags } from 'basehub/react-icon'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import QuickPinchZoom, { make3dTransformValue } from 'react-quick-pinch-zoom'
import { useSyncedValue } from '~/hooks/use-synced-value'
import { clsx } from 'clsx'
import { TextAreaProps } from '~/sotano-kit/primitives/textarea'
import { useSelectedVariants } from '~/hooks/use-selected-variants'
import {
  getValueBasedOnSelectedVariants,
  getBlockUpdateOverride,
  SvgValidator,
} from '@basehub/data/replicache/index'
import { useRepoUndoManager } from '~/replicache/frontend/undo-redo/manager'
import { UpdateBlockParams } from '@basehub/data/replicache/mutators/repo-space/block/mutators/crud'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { useTabsStore } from '../../../tabs/store'
import { useEditorMode } from '../../../editor-mode-provider'
import { autocompletion } from '@codemirror/autocomplete'

import s from './input.module.scss'
import { mergeRefs } from '~/lib/merge-refs'
import { toast } from 'sonner'
import { SVGIcon } from '~/components/icons/svg'
import {
  BlockLabelMoreActionsPortal,
  useBlockInputBaseProps,
} from '../../block-wrapper'
import { CodeEditor } from '~/sotano-kit/primitives/code-editor'
import { MagnifyingGlassIcon, TextAlignLeftIcon } from '@radix-ui/react-icons'
import { useHasRendered } from '~/hooks/use-has-rendered'
import { Button } from '~/sotano-kit/primitives/button'
import { Input } from '~/sotano-kit/primitives/input'
import PinchZoom from 'react-quick-pinch-zoom/esm/PinchZoom/component'
import { ComboboxWithTabs } from '~/sotano-kit/primitives/combobox-tabs'
import { useIconLibrary } from '../use-icon-library'
import { useEditorStore } from '../../../editor-store'
import { isHotkey } from 'is-hotkey'
import { ReactCodeMirrorRef } from '@uiw/react-codemirror'
import { useMutation } from '@tanstack/react-query'
import { env } from '~/env'

export const IconBlockInput = ({
  block,
  path,
  parent,
  readOnly,
  showControls,
  ...props
}: {
  block: IconBlock
  path: string
  parent: Block
  showControls: boolean
} & Omit<TextAreaProps, 'style'>) => {
  const { data } = useRepoReplicache()
  const replicache = data?.replicache
  const inputRef = React.useRef<ReactCodeMirrorRef>(null)
  const editorMode = useEditorMode()
  const selectedDashboardVariants = useSelectedVariants({
    parentBlock: parent,
    block,
  })
  const isSelected = useEditorStore(
    (s) => s.selectedBlockId === idToSourceBlockId(block)
  )
  const containerRef = React.useRef<HTMLDivElement>(null)
  const lib = useIconLibrary()
  const baseProps = useBlockInputBaseProps(block)
  const [isResizing, setIsResizing] = React.useState(false)
  const hasRendered = useHasRendered()
  const comboboxInputRef = React.useRef<HTMLInputElement>(null)
  const _value = React.useMemo(
    () =>
      getValueBasedOnSelectedVariants({
        block,
        variantsToFilterBy: selectedDashboardVariants,
      }) ?? '',
    [block, selectedDashboardVariants]
  )
  const { mutate: fetchIconSvgString } = useMutation(
    async (iconHash: string) => {
      return await fetch(`${env.NEXT_PUBLIC_API_URL}/icon-library/${iconHash}`)
        .then((r) => r.text())
        .then((r) => z.string().parse(r))
    },
    {
      onSuccess(data) {
        const svgString = data
        formatSvg(svgString)
      },
    }
  )
  const undoManager = useRepoUndoManager()
  const activeTabKey = useTabsStore((s) => s.activeTab?.key)
  const { onChange, ref, value } = useSyncedValue<ReactCodeMirrorRef>({
    value: _value,
    customEventsKey: `text-block-input-sync-${idToSourceBlockId(block)}`,
    setValue: React.useCallback(
      async (v, { internalValueRef, customEventsKey }) => {
        if (!replicache?.mutate) return

        if (v.length > 1_500_000) {
          toast.error('Icon is too large (max ~1.5MB)')
          return
        }

        const getParams = (v: string): UpdateBlockParams => ({
          id: block.id,
          instanceChildTarget: block.instanceChildTarget,
          data: getBlockUpdateOverride({
            block,
            newValue: v === '' ? null : v,
            selectedVariants: selectedDashboardVariants,
          }),
          path,
          strategy: 'merge-deep',
          isoNow: new Date().toISOString(),
        })

        const { undo } = await replicache.mutate.updateBlock(getParams(v))
        if (undo) {
          undoManager.current?.push({
            redo: [
              {
                op: 'focus',
                args: {
                  focus: {
                    type: 'block:value',
                    blockId: idToSourceBlockId({
                      id: block.id,
                      sourceBlockId: block.sourceBlockId,
                    }),
                  },
                  preferredTabKey: activeTabKey,
                },
              },
              { op: 'updateBlock', args: getParams(internalValueRef.current) },
              {
                op: 'customWindowEvent',
                args: { name: customEventsKey, detail: { action: 'sync' } },
              },
            ],
            undo: [
              {
                op: 'focus',
                args: {
                  focus: {
                    type: 'block:value',
                    blockId: idToSourceBlockId({
                      id: block.id,
                      sourceBlockId: block.sourceBlockId,
                    }),
                  },
                  preferredTabKey: activeTabKey,
                },
              },
              { op: 'blockBatchMutations', args: { mutations: undo } },
              {
                op: 'customWindowEvent',
                args: { name: customEventsKey, detail: { action: 'sync' } },
              },
            ],
          })
        }
      },
      [
        replicache?.mutate,
        block,
        selectedDashboardVariants,
        path,
        undoManager,
        activeTabKey,
      ]
    ),
    rateLimitMs: 1000,
  })

  const isReadOnly = readOnly || editorMode === 'read'
  const formatSvg = React.useCallback(
    async (svgString?: string) => {
      const newVal = svgString || value
      const formattedCode = await format(newVal, 'html')
      if (formattedCode !== newVal) onChange(formattedCode)
      const view = inputRef.current?.view
      if (!view) return
      inputRef.current?.editor?.focus()
    },
    [onChange, value]
  )

  const onUploadSvg = (
    e: React.DragEvent<HTMLElement> | React.ChangeEvent<HTMLInputElement>
  ) => {
    e.preventDefault()
    e.stopPropagation()
    e.currentTarget.classList.remove('bg-dark-control-base/15')

    // Get the dropped files
    const files =
      e.target instanceof HTMLInputElement
        ? (e.target.files ?? [])
        : (e as React.DragEvent<HTMLElement>).dataTransfer.files

    // Loop through dropped files
    for (const file of files) {
      // Check if the file is an SVG
      if (file.type === 'image/svg+xml') {
        // Create a FileReader to read the SVG file
        const reader = new FileReader()

        reader.onload = (e) => {
          // e.target.result contains the SVG string
          if (!e.target) return
          const svgString = e.target.result
          if (typeof svgString !== 'string') return
          formatSvg(svgString)
        }

        reader.onerror = (e) => {
          console.error('Error reading file', e)
        }

        // Read the file as text
        reader.readAsText(file)
      } else {
        toast.error('Invalid file type. Please upload an .svg file.')
      }
    }
  }

  React.useEffect(() => {
    if (!isSelected) return
    const handlePasteNew = (e: ClipboardEvent) => {
      const newValue = e.clipboardData?.getData('text')
      if (!newValue || !containerRef.current) return
      if (containerRef.current.contains(e.target as Node)) return

      onChange(newValue)
    }
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isHotkey('meta+f', e)) {
        e.preventDefault()
        e.stopPropagation()
        comboboxInputRef.current?.focus()
      } else if (isHotkey('meta+s', e)) {
        e.preventDefault()
        e.stopPropagation()
        formatSvg()
      }
    }

    document.addEventListener('paste', handlePasteNew)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('paste', handlePasteNew)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [formatSvg, isSelected, onChange])

  const allowedLibs = !block.constraints.allowedLibraries
    ? (lib.data?.libs.items ?? [])
    : (lib.data?.libs.items ?? []).filter((l) => {
        const allowedEntries = Object.entries(
          block.constraints.allowedLibraries ?? []
        )
        const foundInAllowed = allowedEntries.find(
          ([value]) => value === l.value
        )
        return !foundInAllowed || foundInAllowed[1]
      })

  const onResizeStart = React.useCallback(() => setIsResizing(true), [])
  const onResizeEnd = React.useCallback(() => setIsResizing(false), [])

  return (
    <>
      {hasRendered && showControls && !isReadOnly && (
        <BlockLabelMoreActionsPortal
          blockId={block.id}
          className="flex items-center gap-1"
        >
          <Button
            unstyled
            focusStyled
            className="!-outline-offset-1 hover:bg-dark-shade h-[30px] cursor-default text-xs font-medium rounded-md text-dark-faint w-full text-left flex items-center py-1 px-2 gap-1 max-w-max"
            onPointerDown={(e) => {
              e.preventDefault()
              formatSvg(value)
            }}
          >
            <TextAlignLeftIcon />
            Format
          </Button>

          {(block.constraints.allowedLibraries === null ||
            Object.values(block.constraints.allowedLibraries ?? {}).some(
              (v) => v
            ) ||
            !!Object.values(allowedLibs.filter((lib) => lib.icons.items.length))
              .length) && (
            <ComboboxWithTabs
              ref={comboboxInputRef}
              allTab={allowedLibs.length > 1}
              className="max-w-[160px] text-xs !max-h-[30px]"
              handleSelect={(v) => fetchIconSvgString(v.id)}
              inputProps={{
                size: 'sm',
                disabled: lib.isLoading,
                icon: <MagnifyingGlassIcon />,
                placeholder: lib.isLoading
                  ? 'Loading libraries...'
                  : 'Search in libraries',
              }}
              tabs={allowedLibs.map((l) => ({
                id: l._id,
                label: l._title,
                items: l.icons.items.map((i) => ({
                  id: i._sys.hash,
                  label: i._title,
                  icon: `${env.NEXT_PUBLIC_API_URL}/icon-library/${i._sys.hash}`,
                })),
              }))}
            />
          )}
        </BlockLabelMoreActionsPortal>
      )}

      <div ref={containerRef} className={clsx('flex', s['container'])}>
        <ResizableCodeEditorContainer
          onResizeStart={onResizeStart}
          onResizeEnd={onResizeEnd}
        >
          <CodeEditor
            // eslint-disable-next-line react-compiler/react-compiler
            ref={mergeRefs([ref, inputRef])}
            data-disabled={isReadOnly}
            aria-disabled={isReadOnly}
            language="html"
            role="textbox"
            {...baseProps}
            {...props}
            extensions={React.useMemo(
              () => [
                autocompletion({
                  closeOnBlur: false,
                  override: [
                    (context) => {
                      const everything = context.matchBefore(/.*$/m)
                      const word = context.matchBefore(/<[^>]*$/m) // stuff inside <>
                      const isClosingTag = context.matchBefore(/<\/[^>]*$/m) // stuff inside <>

                      if (
                        !word &&
                        !isClosingTag &&
                        (!everything || !context.explicit)
                      ) {
                        return null
                      }

                      return {
                        from: word ? word.from + 1 : (everything?.to ?? 0),
                        options: supportedSvgTags.map((tag) => {
                          const isClosingTag = context.matchBefore(/<\/[^>]*$/m)

                          let boost = 1
                          if (tag === 'svg') {
                            boost = 3
                          } else if (!tag.startsWith('fe')) {
                            boost = 2
                          }

                          return {
                            label: isClosingTag ? tag : tag + `></${tag}>`,
                            type: 'class',
                            boost,
                            displayLabel: tag,
                          }
                        }),
                      }
                    },
                  ],
                }),
              ],
              []
            )}
            onChange={(v) => onChange(v)}
            value={value}
          />
        </ResizableCodeEditorContainer>

        <IconBlockPreview
          svg={value}
          onUploadSvg={onUploadSvg}
          enableControls={!isResizing}
        />
      </div>
    </>
  )
}

export const ResizableCodeEditorContainer = ({
  children,
  onResizeStart,
  onResizeEnd,
}: {
  children: React.ReactNode
  onResizeStart?: () => void
  onResizeEnd?: () => void
}) => {
  const [isResizing, setIsResizing] = React.useState(false)
  const handleMouseDown: React.MouseEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsResizing(true)
    onResizeStart?.()
  }
  const [dimensions, setDimensions] = React.useState({ width: 50 })
  const containerRef = React.useRef<HTMLDivElement>(null)
  const handleMouseMove = React.useCallback(
    (e: MouseEvent) => {
      if (!isResizing || !containerRef.current) return

      const parent = containerRef.current.parentElement
      if (!parent) return
      const rect = parent.getBoundingClientRect()

      const newWidth = ((e.clientX - rect.left) / rect.width) * 100
      setDimensions((prev) => ({
        ...prev,
        width: Math.max(20, Math.min(80, newWidth)),
      }))
    },
    [isResizing]
  )

  const handleMouseUp = React.useCallback(() => {
    setIsResizing(false)
    onResizeEnd?.()
  }, [onResizeEnd])

  React.useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    }
    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [handleMouseMove, handleMouseUp, isResizing])

  return (
    <div
      className="relative z-[60] flex flex-col"
      ref={containerRef}
      style={{ width: `${dimensions.width}%` }}
    >
      <div
        onMouseDown={handleMouseDown}
        className={clsx(
          'absolute z-50 -right-0.5 cursor-col-resize h-full w-1 top-1/2 -translate-y-1/2',
          'after:pointer-events-none after:content-[""] after:absolute after:left-1/2 after:-translate-x-1/2 after:h-full',
          isResizing
            ? 'after:bg-dark-control-base after:w-0.5'
            : 'after:bg-dark-border-solid after:w-px'
        )}
      />
      <div
        onMouseDown={handleMouseDown}
        className={clsx(
          'absolute z-50 -right-2 cursor-col-resize border border-dark-border-solid rounded-full h-7 w-4 bg-dark-base-dark top-1/2 -translate-y-1/2',
          'after:w-px after:rounded-full after:top-1 after:bg-dark-faint after:bottom-1 after:left-[35%] after:-translate-x-1/2 after:content-[""] after:absolute',
          'before:w-px before:rounded-full before:top-1 before:bg-dark-faint before:bottom-1 before:left-[65%] before:-translate-x-1/2 before:content-[""] before:absolute'
        )}
      />

      <div
        className="flex-1 [&>div]:h-full"
        onPointerDown={(e) => {
          e.stopPropagation()

          if (
            e.target instanceof HTMLElement &&
            e.target.classList.contains('cm-editor')
          ) {
            const content = e.target.querySelector('[contenteditable]')
            if (content && content instanceof HTMLElement) {
              e.preventDefault()
              content.focus()
            }
          }
        }}
      >
        {children}
      </div>
    </div>
  )
}

export const IconBlockPreview = ({
  enableControls = false,
  svg,
  readonly,
  onUploadSvg,
}: {
  enableControls?: boolean
  svg: string
  readonly?: boolean
  onUploadSvg?: (
    e: React.DragEvent<HTMLElement> | React.ChangeEvent<HTMLInputElement>
  ) => void
}) => {
  const svgRef = React.useRef<HTMLSpanElement>(null)
  const uploadInputRef = React.useRef<HTMLInputElement>(null)
  const isValid = SvgValidator.isValid(svg)
  const [wasZoomed, setWasZoomed] = React.useState(false)
  const quickPinchZoomRef = React.useRef<PinchZoom>(null)
  const svgWrapperRef = React.useRef<HTMLDivElement>(null)
  const dropListeners = readonly
    ? {}
    : {
        onDragLeave: (e: React.DragEvent<HTMLElement>) => {
          e.preventDefault()
          e.stopPropagation()

          // Check if leaving the current target or its children
          if (
            !e.currentTarget.contains(e.relatedTarget as Node) &&
            e.currentTarget !== e.relatedTarget
          ) {
            e.currentTarget.classList.remove('bg-dark-control-base/15')
          }
        },
        onDragEnter: (e: React.DragEvent<HTMLElement>) => {
          e.preventDefault()
          e.stopPropagation()
          e.currentTarget.classList.add('bg-dark-control-base/15')
        },
        onDragOver: (e: React.DragEvent<HTMLElement>) => {
          e.preventDefault()
          e.stopPropagation()
        },
        onDrop: onUploadSvg,
      }

  const handleUpdate = React.useCallback(
    ({ x, y, scale }: { x: number; y: number; scale: number }) => {
      const { current: svgNode } = svgRef

      if (svgNode && enableControls) {
        const value = make3dTransformValue({
          x,
          y,
          scale,
        })
        svgNode.style.setProperty('transform', value)
        setWasZoomed((prev) => {
          if (prev) return true
          return x !== 0 && y !== 0 && scale !== 1
        })
      }
    },
    [enableControls]
  )

  const center = () => {
    if (!quickPinchZoomRef.current) return
    quickPinchZoomRef.current?.scaleTo({
      scale: 1,
      x: 0,
      y: 0,
      duration: 300,
      animated: true,
    })
    setTimeout(() => {
      setWasZoomed(false)
    }, 400)
  }

  React.useEffect(() => {
    window.addEventListener('resize', center, {
      passive: true,
      capture: true,
    })

    return () => {
      window.removeEventListener('resize', center)
    }
  }, [])

  return (
    <div className="relative flex-1 border-dark-border-solid w-fit overflow-hidden">
      <span
        className="absolute inset-0 invert dark:invert-0 pointer-events-none select-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='208' height='208' viewBox='0 0 208 208' fill='none' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3E%3Crect width='208' height='208' fill='url(%23pattern0_13138_40641)'/%3E%3Cdefs%3E%3Cpattern id='pattern0_13138_40641' patternContentUnits='objectBoundingBox' width='1' height='1'%3E%3Cuse xlink:href='%23image0_13138_40641' transform='matrix(0.012603 0 0 0.0124762 -4.54121 -3.99143)'/%3E%3C/pattern%3E%3Cimage id='image0_13138_40641' width='800' height='720' xlink:href='data:image/png;base64,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'/%3E%3C/defs%3E%3C/svg%3E%0A")`,
          backgroundRepeat: 'repeat',
          backgroundSize: '64px',
        }}
      />

      <div
        {...dropListeners}
        ref={svgWrapperRef}
        data-svg-wrapper
        className="flex absolute items-center left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 justify-center z-50"
        style={{
          width: '100vw',
          height: '100vh',
        }}
      >
        <QuickPinchZoom
          onUpdate={handleUpdate}
          ref={quickPinchZoomRef}
          inertia={false}
          enabled={enableControls}
          setOffsetsOnce={false}
          centerContained
          animationDuration={0}
          enforceBoundsDuringZoom
          draggableUnZoomed
          wheelScaleFactor={100}
          minZoom={1}
          containerProps={{
            className: clsx(
              'h-full flex-1 z-40',
              isValid ? 'cursor-grab' : 'cursor-default'
            ),
          }}
        >
          {!svg ? (
            <div
              onClick={() => uploadInputRef.current?.click()}
              className="h-full flex justify-center items-center flex-col gap-1"
            >
              {!readonly && (
                <p className="text-dark-faint text-sm font-medium pointer-events-none">
                  Choose or drop an .svg file
                </p>
              )}
            </div>
          ) : isValid ? (
            <span
              ref={svgRef}
              className="flex h-full flex-1 justify-center items-center"
            >
              <Icon
                content={svg}
                components={{
                  svg: (props) => <svg {...props} className="max-w-[200px]" />,
                }}
              />
            </span>
          ) : (
            <div
              onClick={() => uploadInputRef.current?.click()}
              className="text-dark-error h-full flex justify-center items-center flex-col gap-1"
            >
              <SVGIcon className="size-5 pointer-events-none" />
              <p className="text-sm font-medium pointer-events-none">
                Invalid markup for SVG
              </p>
            </div>
          )}
        </QuickPinchZoom>
      </div>

      {wasZoomed && (
        <Button
          unstyled
          focusStyled
          className="!-outline-offset-1 hover:bg-dark-shade cursor-default text-xs font-medium rounded-md text-dark-faint w-full text-left flex items-center py-1 px-2 gap-1 max-w-max absolute top-2 right-2 z-50"
          onClick={center}
        >
          Center
        </Button>
      )}

      <Input
        ref={uploadInputRef}
        type="file"
        readOnly={readonly}
        disabled={readonly}
        accept={'.svg'}
        className="hidden"
        onChange={onUploadSvg}
      />
    </div>
  )
}
